import {
  Controller,
  SubmitHandler,
  useForm,
  useFormState,
} from "react-hook-form";

import { FormInput, InputDate, SelectForm } from "../../../../components/Input";
import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  discardCopyOf,
} from "../../../../components/FormPanel";
import {
  AccountMultiSelect,
  AccountOption,
} from "../../../../components/AccountMultiSelect";
import {
  buildDraftDetails,
  ConfirmDetail,
  formatOptionalCurrency,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import { DateFormat, formatDate } from "../../../../utils";
import {
  CreateSavingsGoalPayload,
  SavingsGoalMode,
  SavingsGoalResponse,
} from "../../../../services";
import { MobileSavingsGoalForm } from "./MobileSavingsGoalForm";
import {
  DEFAULT_SAVINGS_GOAL,
  FORM_ID,
  MODE_OPTIONS,
  modeOptionOf,
} from "./constants";
import { rollForwardValuesOf } from "./rollForward";
import { SavingsGoalFormValues, SavingsGoalPanelProps } from "./types";

const headingsOf = (isEdit: boolean, isRollForward: boolean) => {
  if (isEdit) {
    return {
      eyebrow: "EDIT SAVINGS GOAL",
      title: "Edit savings goal",
      mobileTitle: "Edit goal",
    };
  }
  if (isRollForward) {
    return {
      eyebrow: "ROLL FORWARD SAVINGS GOAL",
      title: "Roll forward savings goal",
      mobileTitle: "Roll forward goal",
    };
  }
  return {
    eyebrow: "NEW SAVINGS GOAL",
    title: "Create savings goal",
    mobileTitle: "New goal",
  };
};

const defaultValuesOf = (
  goal: SavingsGoalResponse | undefined,
  rollForwardFrom: SavingsGoalResponse | undefined,
  accounts: AccountOption[],
): SavingsGoalFormValues => {
  if (rollForwardFrom) {
    return rollForwardValuesOf(rollForwardFrom, accounts);
  }
  if (goal) {
    return {
      name: goal.name,
      targetAmount: goal.targetAmount,
      targetDate: new Date(goal.targetDate),
      mode: modeOptionOf(goal.mode),
      accountIds: goal.accountIds,
    };
  }
  return DEFAULT_SAVINGS_GOAL;
};

export const SavingsGoalPanel = ({
  goal,
  rollForwardFrom,
  accounts,
  isOpen,
  onClose,
  onSubmit,
}: SavingsGoalPanelProps) => {
  const isEdit = Boolean(goal);
  const isRollForward = Boolean(rollForwardFrom);

  const defaultValues = defaultValuesOf(goal, rollForwardFrom, accounts);

  const form = useForm<SavingsGoalFormValues>({
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues } = form;

  const { isSubmitting } = useFormState({ control });

  const submit: SubmitHandler<SavingsGoalFormValues> = (data) => {
    const payload: CreateSavingsGoalPayload = {
      name: data.name,
      targetAmount: Number(data.targetAmount),
      targetDate: formatDate(data.targetDate),
      mode: data.mode.value as SavingsGoalMode,
      accountIds: data.accountIds,
      ...(rollForwardFrom && { previousGoalId: rollForwardFrom.id }),
    };
    return onSubmit(payload, goal?.id);
  };

  const { eyebrow, title, mobileTitle } = headingsOf(isEdit, isRollForward);
  const actionLabel = isEdit ? "Save changes" : "Create goal";
  const mobileActionLabel = isEdit ? "Save" : "Create";

  const buildDiscardDetails = (): ConfirmDetail => {
    const values = getValues();
    const hasTarget = Number(values.targetAmount) > 0;
    const targetAmount = hasTarget
      ? formatOptionalCurrency(values.targetAmount)
      : undefined;

    if (isEdit) {
      return {
        pill: { label: "SAVINGS GOAL", variant: "DEFAULT" },
        title: values.name || goal?.name || "Savings goal",
        subtitle: joinDetailParts(
          formatDate(values.targetDate, DateFormat.DMY_SPACE),
        ),
        amount: targetAmount,
        amountVariant: "neutral",
      };
    }

    return buildDraftDetails({
      noun: "savings goal",
      fieldValues: [
        values.name,
        hasTarget ? values.targetAmount : null,
        values.targetDate,
      ],
      amount: targetAmount,
      primaryLabel: values.name,
    });
  };

  const discard = {
    ...discardCopyOf(isEdit, "savings goal"),
    details: buildDiscardDetails,
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Savings goal form"
      eyebrow={eyebrow}
      title={title}
      form={form}
      resetValues={defaultValues}
      formId={FORM_ID}
      onSubmit={submit}
      submitLabel={actionLabel}
      discard={discard}
      mobile={{
        title: mobileTitle,
        submitLabel: mobileActionLabel,
        fields: (
          <MobileSavingsGoalForm
            control={control}
            accounts={accounts}
            isSubmitting={isSubmitting}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="savings-goal-name">Name *</FieldLabel>
          </LabelRow>
          <FormInput
            id="savings-goal-name"
            required
            name="name"
            control={control}
            handleClear={false}
            placeholder="e.g. Emergency Fund"
            autoFocus={true}
            rules={{
              required: "Name is required!",
              minLength: {
                value: 3,
                message: "Name should have more than 3 letters!",
              },
            }}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="savings-goal-target-amount">
              Target amount (€) *
            </FieldLabel>
          </LabelRow>
          <FormInput
            id="savings-goal-target-amount"
            required
            name="targetAmount"
            control={control}
            type="number"
            handleClear={false}
            placeholder="e.g. 10000"
            rules={{
              required: "Target amount is required!",
              min: { value: 1, message: "Target must be greater than 0!" },
            }}
          />
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="savings-goal-target-date">
              Target date *
            </FieldLabel>
          </LabelRow>
          <InputDate
            id="savings-goal-target-date"
            name="targetDate"
            control={control}
            rules={{ required: "Target date is required!" }}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>When you want to reach this goal.</FieldHelper>
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="savings-goal-mode">Goal type *</FieldLabel>
          </LabelRow>
          <SelectForm
            id="savings-goal-mode"
            name="mode"
            control={control}
            defaultValue={defaultValues.mode}
            options={MODE_OPTIONS}
            rules={{ required: "Goal type is required!" }}
          />
          <FieldHelper>
            Track a total balance, or an amount saved this year.
          </FieldHelper>
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Linked savings accounts *</FieldLabel>
          </LabelRow>
          <Controller
            name="accountIds"
            control={control}
            rules={{
              validate: (value: number[]) =>
                value.length > 0 || "Link at least one account!",
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <AccountMultiSelect
                  accounts={accounts}
                  value={value}
                  onChange={onChange}
                  emptyLabel="No savings accounts available."
                />
                {error && <FieldHelper>{error.message}</FieldHelper>}
              </>
            )}
          />
          <FieldHelper>Progress is measured from these accounts.</FieldHelper>
        </Field>
      </FormCard>
    </FormPanel>
  );
};
